import React from "react";
import { Link, NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { OrderContact } from "@/components/OrderContact";
import { Briefcase, ShoppingCart, User, Loader2, Clock, LayoutGrid } from "lucide-react";
import { ProProvider, usePro } from "@/context/ProContext";

const tab = ({ isActive }) =>
  `inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
    isActive ? "bg-[var(--oz-dark)] text-white" : "text-[var(--oz-muted)] hover:text-[var(--oz-text)]"
  }`;

const ProNav = () => {
  const { user, count } = usePro();
  return (
    <div className="border-b border-[var(--oz-border)] bg-white/70 backdrop-blur sticky top-16 z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-3 flex items-center justify-between gap-3">
        <Link to="/espace-pro" data-testid="pro-nav-home" className="flex items-center gap-2 font-serif text-lg text-[var(--oz-text)] whitespace-nowrap">
          <Briefcase className="w-5 h-5 text-[var(--oz-gold)]" /> Espace Pro
        </Link>
        <nav className="flex items-center gap-1">
          <NavLink end to="/espace-pro" className={tab} data-testid="pro-nav-catalog"><LayoutGrid className="w-4 h-4" /> <span className="hidden sm:inline">Catalogue</span></NavLink>
          <NavLink to="/espace-pro/panier" className={tab} data-testid="pro-nav-cart">
            <ShoppingCart className="w-4 h-4" /> <span className="hidden sm:inline">Panier</span>
            {count > 0 && <span data-testid="pro-cart-count" className="bg-[var(--oz-gold)] text-[var(--oz-dark)] text-[11px] rounded-full px-1.5">{count}</span>}
          </NavLink>
          <NavLink to={user ? "/espace-pro/compte" : "/espace-pro/connexion"} className={tab} data-testid="pro-nav-account">
            <User className="w-4 h-4" /> <span className="hidden sm:inline">{user ? "Mon compte" : "Connexion"}</span>
          </NavLink>
        </nav>
      </div>
    </div>
  );
};

export const PendingNotice = () => (
  <div data-testid="pro-pending-notice" className="flex gap-3 rounded-2xl border border-[var(--oz-gold)] bg-[#FBF6EC] p-5 text-sm text-[var(--oz-text)]">
    <Clock className="w-5 h-5 text-[var(--oz-gold)] flex-shrink-0" />
    <p>Votre compte professionnel est en attente de validation. Vous recevrez un e-mail dès qu'il sera activé : les prix professionnels et la commande seront alors accessibles.</p>
  </div>
);

export const RequireApproved = ({ children }) => {
  const { user, approved } = usePro();
  const loc = useLocation();
  if (user === null) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--oz-gold)]" /></div>;
  if (!user) return <Navigate to={`/espace-pro/connexion?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (!approved) return <div className="max-w-3xl mx-auto px-4 py-16"><PendingNotice /></div>;
  return children;
};

export const RequireUser = ({ children }) => {
  const { user } = usePro();
  const loc = useLocation();
  if (user === null) return <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--oz-gold)]" /></div>;
  if (!user) return <Navigate to={`/espace-pro/connexion?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return children;
};

export default function ProLayout() {
  return (
    <ProProvider>
      <div data-testid="pro-layout" className="min-h-[70vh]">
        <ProNav />
        <Outlet />
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10 border-t border-[var(--oz-border)]">
          <OrderContact title="Une question ou une commande hors ligne ? Contactez directement Ozentya" testid="pro-order-contact" />
        </div>
      </div>
    </ProProvider>
  );
}
